import {t, Trans} from '@lingui/macro';
import produce, {castDraft} from 'immer';
import {pickBy} from 'lodash';
import React, {ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {InfoBanner} from '../../../../components/infoBanner/infoBanner';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {Parameter} from '../../../editors/commons/parameterModel';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {MessageBodyCondition, MessageHtmlBodyCondition, RuleCondition} from '../../ruleConditionModel';
import {RuleConditionDefinition} from '../../ruleParameterModel';
import {RuleConditionIcon} from '../ruleConditionIcon';
import {getRuleLabel} from '../ruleConditionProPill';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {renderGenericRuleParameters} from './ruleDefaultActionConditionCard';

/*
 * Types.
 */

interface RuleMessageBodyConditionCardProps {
  condition: MessageBodyCondition | MessageHtmlBodyCondition;
  conditionDefinition: RuleConditionDefinition | undefined;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  onUpdate: HandlerOf<RuleCondition>;
  onDelete?: HandlerOf<RuleCondition>;
}

/*
 * Styles.
 */

const InfoIcon = styled(Icon)`
  margin: 2px 0 0 8px;
`;

const TooltipContent = styled.div`
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.palette.teal.shade50};
  line-height: 1.5;
  color: ${(p) => p.theme.greys.white};
`;

/*
 * Component.
 */

export const RuleMessageBodyConditionCard = (props: RuleMessageBodyConditionCardProps) => {
  const {condition, conditionDefinition, errorMessage, isDisabled, onUpdate, onDelete} = props;
  const localize = useLocalize();
  const [updatedCondition, setUpdatedCondition] = useState(false);

  const canUseIncludeQuoteParam = condition.params.include_quote === undefined && !updatedCondition;

  // This should not happen, but safety check.
  const errorLabel = <Trans id="definition-condition-not-found">Condition {condition.type} not found</Trans>;
  if (!conditionDefinition) {
    return <RuleMultilineParameterCard icon={<RuleConditionIcon id={condition.type} />} title={errorLabel} />;
  }

  // Remove PRO/PRIME/GROWTH mention.
  const {label} = getRuleLabel(conditionDefinition.name);

  const onCancel = onDelete ? () => onDelete(condition) : undefined;

  const publicParameters = pickBy(conditionDefinition.parameters, Parameter.isPublic);

  const {
    parameters: [comparatorParameter, ...otherParameters],
    collapsableParameters,
  } = renderGenericRuleParameters(
    condition,
    conditionDefinition,
    RuleDefaultCardTypesEnum.CONDITION,
    onUpdate,
    publicParameters,
    isDisabled,
  );

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>{label}</MultilineRuleParameterCardDefaultTitleStyled>
      {comparatorParameter}
    </>
  );

  const handleUpdate = () => {
    onUpdate(
      produce(condition, (draft) => {
        castDraft(draft).params.include_quote = false;
      }),
    );
    setUpdatedCondition(true);
  };

  return (
    <RuleMultilineParameterCard
      icon={<RuleConditionIcon id={condition.type} />}
      onCancel={onCancel}
      title={title}
      collapsableParameters={
        canUseIncludeQuoteParam ? [renderUpdateBanner(handleUpdate, localize)] : collapsableParameters
      }
      errorMessage={errorMessage}
    >
      {otherParameters}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

function renderUpdateBanner(handleCtaClick: Handler, localize: Localizer) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <InfoBanner
          emoji={<Icon name="giftSmall" color={theme.palette.blue.shade40} />}
          buttons={[{onClick: handleCtaClick, label: localize(t('rule-message-body-update-button')`Update`)}]}
          key="update-banner"
        >
          A new version of this condition is available
          <TooltipCoordinator
            render={() => (
              <TooltipContent>
                <Trans id="rule-message-body-tooltip">
                  Select whether to include the past email history of the thread when the rule scans the new
                  inbound and/or outbound message.
                </Trans>
              </TooltipContent>
            )}
          >
            <InfoIcon name="infoSmall" color={theme.palette.blue.shade40} />
          </TooltipCoordinator>
        </InfoBanner>
      )}
    </UserThemeConsumer>
  );
}
